import type { ActionReturnType } from '../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  DEBUG,
  LANG_CACHE_NAME,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { SSG_VERSION } from '../../../config/ssgVersion';
import { updateAppBadge } from '../../../util/appBadge';
import { reportAttrRegister } from '../../../util/attrReport';
import { getDeviceType } from '../../../util/browser/shellContainer';
import {
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, MAX_MEMORY_DOWNLOAD_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { recordAuthDiagnostic } from '../../../util/clientDiagnostics';
import { GLOBAL_ACTION_TEXT } from '../../../util/localization/customUiTexts.global';
import { pickCustomUiText } from '../../../util/localization/customUiTexts.shared';
import { ACCOUNT_SLOT, getAccountsInfo } from '../../../util/multiaccount';
import { callEventLogoutNative, callEventRegisterNative, getChannelCode } from '../../../util/nativeBridge';
import { unsubscribe } from '../../../util/notifications';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, destroyApi, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { removeGlobalFromCache } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

function t(global: Parameters<typeof selectSharedSettings>[0], key: keyof typeof GLOBAL_ACTION_TEXT) {
  return pickCustomUiText(GLOBAL_ACTION_TEXT[key], { code: selectSharedSettings(global).language });
}

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo).map(({ userId }) => userId)?.filter(Boolean);

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData: loadStoredSession(),
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    maxMemoryDownloadSize: MAX_MEMORY_DOWNLOAD_SIZE,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
    deviceType: getDeviceType(),
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
});

const AUTH_LOADING_TIMEOUT_MS = 15000;

function withSsgAuthTimeout<T>(promise: Promise<T>, label: string) {
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error(`${label}_TIMEOUT`));
      }, AUTH_LOADING_TIMEOUT_MS);
    }),
  ]);
}

function scheduleAuthLoadingTimeout() {
  setTimeout(() => {
    // eslint-disable-next-line eslint-multitab-tt/no-immediate-global
    let global = getGlobal();
    if (!global.authIsLoading) return;
    if (global.authState === 'authorizationStateReady') return;

    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
  }, AUTH_LOADING_TIMEOUT_MS);
}

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  return {
    ...global,
    authState: 'authorizationStateWaitPhoneNumber',
    authErrorKey: undefined,
    authNeedsProfileSetup: false,
    ssgRegAccount: undefined,
    ssgRegPassword: undefined,
  };
});

function getSsgDeviceId(): string {
  const key = 'ssg_device_id';
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    deviceId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

const SSG_REGISTER_EVENT_SENT_KEY = 'ssg_register_event_sent';
const SSG_JUST_REGISTERED_KEY = 'ssgram_just_registered';

function removePersistentFlag(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
  }
}

function setPersistentFlag(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}

function getPersistentFlag(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function tryNotifyRegisterNative(): boolean {
  const isNativeNotified = getPersistentFlag(SSG_REGISTER_EVENT_SENT_KEY) === '1';

  // eslint-disable-next-line eslint-multitab-tt/no-immediate-global
  const latestGlobal = getGlobal();
  const uid = latestGlobal.currentUserId;
  if (!uid) return isNativeNotified;

  reportAttrRegister(String(uid));

  if (isNativeNotified) return true;

  callEventRegisterNative({ channelCode: getChannelCode(), uid: String(uid) });
  setPersistentFlag(SSG_REGISTER_EVENT_SENT_KEY, '1');

  return true;
}

export function maskSsgAccount(account?: string) {
  if (!account) return '';
  return account.length <= 4 ? '***' : `${account.slice(0, 2)}***${account.slice(-2)}`;
}

function mapSsgAuthError(err: any): string {
  // eslint-disable-next-line eslint-multitab-tt/no-immediate-global
  const global = getGlobal();
  const message = typeof err?.message === 'string' ? err.message : '';
  const normalized = message.toUpperCase();

  if (normalized.includes('AUTHORIZATION_SIGN_UP_REQUIRED')) {
    return t(global, 'accountNotRegistered');
  }

  if (normalized.includes('PHONE_NUMBER_OCCUPIED') || normalized.includes('ACCOUNT_OCCUPIED')) {
    return t(global, 'accountRegistered');
  }

  if (normalized.includes('IP_NOT_FOUND')) {
    return t(global, 'ipNotFoundError');
  }

  if (normalized.includes('IP_BLACK')) {
    return t(global, 'networkUnavailable');
  }

  if (normalized.includes('IP_UPPER_LIMIT') || normalized.includes('DEVICE_UPPER_LIMIT')) {
    return t(global, 'registerLimitReached');
  }

  if (normalized.includes('FIRSTNAME_INVALID')) {
    return t(global, 'invalidName');
  }

  if (normalized.includes('CAPTCHA_CODE_EXPIRED') || normalized.includes('CAPTCHA_INVALID')) {
    return t(global, 'captchaExpired');
  }

  if (normalized.includes('CAPTCHA_CODE_NOT_MATCH')) {
    return t(global, 'captchaMismatch');
  }

  if (normalized.includes('INVITE_CODE') || normalized.includes('CHANNEL_CODE')) {
    return t(global, 'inviteCodeInvalid');
  }

  if (normalized.includes('ACCOUNT_REQUIRED')) {
    return t(global, 'accountRequired');
  }

  if (normalized.includes('PASSWORD_REQUIRED') || normalized.includes('PASSWORD_EMPTY')) {
    return t(global, 'passwordRequired');
  }

  if (normalized.includes('PASSWORD_NOT_MEET_RULE')) {
    return t(global, 'passwordRuleInvalid');
  }

  if (normalized.includes('ACCOUNT_RESTRICTED')) {
    return t(global, 'accountRestricted');
  }

  if (normalized.includes('PASSWORD_MISMATCH')) {
    return t(global, 'passwordMismatch');
  }

  if (
    normalized.includes('PHONE_NUMBER_INVALID')
    || normalized.includes('ACCOUNT_INVALID')
    || normalized.includes('PASSWORD_INVALID')
    || normalized.includes('PASSWORD_NOT_RIGHT')
    || normalized.includes('PASSWORD_HASH_INVALID')
    || normalized.includes('AUTH_KEY_UNREGISTERED')
  ) {
    return t(global, 'invalidAccountOrPassword');
  }

  if (normalized.includes('FLOOD_WAIT') || normalized.includes('TOO_MANY_REQUESTS')) {
    return t(global, 'operationTooFrequent');
  }

  if (
    normalized.includes('NETWORK')
    || normalized.includes('TIMEOUT')
    || normalized.includes('CONNECTION')
    || normalized.includes('DISCONNECT')
    || normalized.includes('NOT INITIALIZED')
    || normalized.includes('REQUEST WAS UNSUCCESSFUL')
  ) {
    return t(global, 'networkErrorRetry');
  }

  return t(global, 'systemBusyRetry');
}

function getSsgCheckAccountRegisterTakenHint() {
  // eslint-disable-next-line eslint-multitab-tt/no-immediate-global
  return t(getGlobal(), 'accountTakenHint');
}

addActionHandler('ssgSignIn', async (global, actions, payload): Promise<void> => {
  const {
    account,
    password,
    captchaCode,
    captchaId,
  } = payload!;

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    if (captchaCode && captchaId) {
      const ok = await callApi('ssgCheckCaptchaCode', captchaCode, captchaId);
      recordAuthDiagnostic('captcha', { success: Boolean(ok), error: ok ? undefined : 'CAPTCHA_INVALID' });
      if (!ok) {
        global = getGlobal();
        global = {
          ...global,
          authIsLoading: false,
          authErrorKey: t(global, 'captchaInvalid'),
        };
        setGlobal(global);
        actions.ssgFetchCaptcha();
        return;
      }
    }

    const deviceId = getSsgDeviceId();
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] ssgSignIn 登录接口参数:', {
        account: maskSsgAccount(account),
        password: password ? '***' : '',
        device: deviceId,
        version: SSG_VERSION,
        captchaCode: captchaCode ? '***' : '(未传)',
        captchaId: captchaId || '(未传)',
      });
    }

    const signInStartedAt = Date.now();
    try {
      await withSsgAuthTimeout(callApi('ssgSignIn', account, password, deviceId), 'SSG_SIGN_IN');
      recordAuthDiagnostic('sign_in', { success: true, duration: Date.now() - signInStartedAt });
    } catch (err: any) {
      recordAuthDiagnostic('sign_in', {
        success: false,
        duration: Date.now() - signInStartedAt,
        error: err,
      });
      throw err;
    }

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error('[SSG] ssgSignIn 登录失败:', err);
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgCheckAccountAndPassword', async (global, actions, payload): Promise<void> => {
  const { account, password } = payload!;
  if (!account?.trim()) return;

  global = getGlobal();
  global = { ...global, checkAccountResult: null, checkAccountErrorHint: undefined };
  setGlobal(global);

  try {
    const trimmedAccount = account.trim();
    const passwordForApi = password || '';
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 请求参数:', {
        account: maskSsgAccount(trimmedAccount),
        hasPassword: Boolean(passwordForApi),
        passwordLength: passwordForApi.length,
      });
    }
    const result = await callApi('ssgCheckAccountAndPassword', trimmedAccount, passwordForApi);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 接口返回:', { result: result === true });
    }
    global = getGlobal();
    if (result === true) {
      global = {
        ...global,
        checkAccountResult: true,
        checkAccountErrorHint: undefined,
      };
    } else {
      global = {
        ...global,
        checkAccountResult: false,
        checkAccountErrorHint: getSsgCheckAccountRegisterTakenHint(),
      };
    }
    setGlobal(global);
  } catch (err: any) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 接口返回(错误):', err?.message ?? err);
    }
    const normalized = typeof err?.message === 'string' ? err.message.toUpperCase() : '';
    const isTakenVerdict = normalized.includes('OCCUPIED');
    global = getGlobal();
    global = {
      ...global,
      checkAccountResult: false,
      checkAccountErrorHint: isTakenVerdict
        ? getSsgCheckAccountRegisterTakenHint()
        : mapSsgAuthError(err),
    };
    setGlobal(global);
  }
});

addActionHandler('ssgResetCheckAccount', (global): ActionReturnType => {
  return {
    ...global,
    checkAccountResult: undefined,
    checkAccountErrorHint: undefined,
  };
});

addActionHandler('ssgFetchCaptcha', async (global): Promise<void> => {
  try {
    const result = await callApi('ssgGetCaptchaCode');

    const res = result as any;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] getCaptchaCode 接口返回:', {
        enable: res?.enable,
        captchaId: res?.captchaId,
        expiredAt: res?.expiredAt,
        bytes: res?.bytes ? `Uint8Array(${res.bytes?.length ?? 0})` : undefined,
      });
    }

    global = getGlobal();
    if (!result) {
      global = { ...global, captchaData: { enable: false, captchaId: '', expiredAt: 0 } };
      setGlobal(global);
      return;
    }

    const bytes = (result as any).bytes;
    global = {
      ...global,
      captchaData: {
        enable: Boolean((result as any).enable),
        bytes: bytes ? (bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)) : undefined,
        captchaId: String((result as any).captchaId || ''),
        expiredAt: Number((result as any).expiredAt || 0),
      },
    };
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    global = {
      ...global,
      captchaData: { enable: false, captchaId: '', expiredAt: 0 },
    };
    setGlobal(global);
  }
});

addActionHandler('ssgCompleteRegistrationPasswordStep', async (global, actions, payload): Promise<void> => {
  const {
    account,
    password,
    captchaCode,
    captchaId,
  } = payload!;
  if (!account?.trim() || !password) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    if (captchaCode && captchaId) {
      const ok = await callApi('ssgCheckCaptchaCode', captchaCode, captchaId);
      recordAuthDiagnostic('captcha', { success: Boolean(ok), error: ok ? undefined : 'CAPTCHA_INVALID' });
      if (!ok) {
        global = getGlobal();
        global = {
          ...global,
          authIsLoading: false,
          authErrorKey: t(global, 'captchaInvalid'),
        };
        setGlobal(global);
        actions.ssgFetchCaptcha();
        return;
      }
    }

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: undefined,
      ssgRegAccount: account.trim(),
      ssgRegPassword: password,
      ssgRegCaptchaCode: captchaCode,
      ssgRegCaptchaId: captchaId,
      authNeedsProfileSetup: true,
    };
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgSignUp', async (global, actions, payload): Promise<void> => {
  const {
    firstName, autoRegister, inviteCode, captchaCode, captchaId,
  } = payload!;

  global = getGlobal();
  const account = global.ssgRegAccount;
  const password = global.ssgRegPassword;
  const regCaptchaCode = captchaCode ?? global.ssgRegCaptchaCode;
  const regCaptchaId = captchaId ?? global.ssgRegCaptchaId;
  if (!autoRegister && (!account || !password)) {
    return;
  }

  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    const deviceId = getSsgDeviceId();
    const trimmedInviteCode = inviteCode?.trim();
    const channelCode = trimmedInviteCode || getChannelCode();
    removePersistentFlag(SSG_REGISTER_EVENT_SENT_KEY);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] ssgSignUp 注册接口参数:', {
        account: autoRegister ? '' : maskSsgAccount(account),
        password: autoRegister ? '(auto)' : (password ? '***' : ''),
        first_name: autoRegister ? '' : (firstName?.trim() || ''),
        device: deviceId,
        version: SSG_VERSION,
        auto_register: Boolean(autoRegister),
        invite_code: trimmedInviteCode || '(未传)',
        download_source: channelCode || '(空)',
        captcha_code: regCaptchaCode ? '***' : '(未传)',
        captcha_id: regCaptchaId || '(未传)',
      });
    }
    const signUpStartedAt = Date.now();
    try {
      await callApi(
        'ssgSignUp',
        account || '',
        password || '',
        firstName?.trim() || '',
        deviceId,
        Boolean(autoRegister),
        channelCode,
        trimmedInviteCode,
        regCaptchaCode,
        regCaptchaId,
      );
      recordAuthDiagnostic('sign_up', { success: true, duration: Date.now() - signUpStartedAt });
    } catch (err: any) {
      recordAuthDiagnostic('sign_up', {
        success: false,
        duration: Date.now() - signUpStartedAt,
        error: err,
      });
      throw err;
    }

    setPersistentFlag(SSG_JUST_REGISTERED_KEY, '1');

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authNeedsProfileSetup: false,
      ssgRegAccount: undefined,
      ssgRegPassword: undefined,
      ssgRegCaptchaCode: undefined,
      ssgRegCaptchaId: undefined,
      captchaData: undefined,
    };
    setGlobal(global);

    if (!tryNotifyRegisterNative()) {
      window.setTimeout(() => {
        tryNotifyRegisterNative();
      }, 300);
      window.setTimeout(() => {
        tryNotifyRegisterNative();
      }, 1000);
    }
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgAutoRegister', async (global, actions, payload): Promise<void> => {
  if (global.authIsLoading) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[SSG] 自动注册判断:', {
    authState: global.authState,
    connectionReady: true,
    authIsLoading: global.authIsLoading,
    willAutoRegister: true,
  });

  await actions.ssgSignUp({
    firstName: '',
    autoRegister: true,
    inviteCode: payload?.inviteCode,
  });
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  recordAuthDiagnostic('sign_out', { success: true });
  callEventLogoutNative({ uid: global.currentUserId });

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await destroyApi();
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('setDeviceToken', (global, actions, deviceToken): ActionReturnType => {
  return {
    ...global,
    push: {
      deviceToken,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});
